type ColorType = "rgb" | "rgba" | "hsl" | "hsla";

// https://stackoverflow.com/questions/596216/formula-to-determine-brightness-of-rgb-color
const MAX_LUMA = 0.2126 * 255 + 0.7152 * 255 + 0.0722 * 255;

/**
 * Returns a number whose value is limited to the given range.
 *
 * @param {number} value The value to be clamped
 * @param {number} min The lower boundary of the output range
 * @param {number} max The upper boundary of the output range
 * @returns {number} A number in the range [min, max]
 */
export function clamp(value: number, min: number = 0, max: number = 1) {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

/**
 * Converts a color from CSS hex format to CSS rgb format.
 *
 * @param {string} color - Hex color, i.e. #nnn or #nnnnnn
 * @returns {string} A CSS rgb color string
 */
export function convertHexToRGB(color: string) {
  color = color.substr(1);

  const re = new RegExp(`.{1,${color.length / 3}}`, "g");
  let colors = color.match(re);

  if (colors && colors[0].length === 1) {
    colors = colors.map((n) => n + n);
  }

  return colors ? `rgb(${colors.map((n) => parseInt(n, 16)).join(", ")})` : "";
}

/**
 * Returns an object with the type and values of a color.
 *
 * Note: Does not support rgb % values.
 *
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns {object} - A MUI color object: {type: string, values: number[]}
 */
export function decomposeColor(color: string): { type: ColorType; values: number[] } {
  if (color.charAt(0) === "#") {
    return decomposeColor(convertHexToRGB(color));
  }

  const marker = color.indexOf("(");
  const type = color.substring(0, marker) as ColorType;
  const valueStrings = color.substring(marker + 1, color.length - 1).split(",");
  const values = valueStrings.map((value) => parseFloat(value));

  if (process.env.NODE_ENV !== "production") {
    if (["rgb", "rgba", "hsl", "hsla"].indexOf(type) === -1) {
      throw new Error(
        [
          `Unsupported \`${color}\` color.`,
          "We support the following formats: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla().",
        ].join("\n"),
      );
    }
  }

  return { type, values };
}

/**
 * Converts a color object with type and values to a string.
 *
 * @param {object} color - Decomposed color
 * @param {string} color.type - One of: 'rgb', 'rgba', 'hsl', 'hsla'
 * @param {array} color.values - [n,n,n] or [n,n,n,n]
 * @returns {string} A CSS color string
 */
export function recomposeColor(color: { type: string; values: (number | string)[] }) {
  const { type } = color;
  let { values } = color;

  if (type.indexOf("rgb") !== -1) {
    // Only convert the first 3 values to int (i.e. not alpha)
    values = values.map((n, i) => (i < 3 ? parseInt(n.toString(), 10) : n));
  }

  if (type.indexOf("hsl") !== -1) {
    values[1] = `${values[1]}%`;
    values[2] = `${values[2]}%`;
  }

  return `${color.type}(${values.join(", ")})`;
}

/**
 * Darkens a color.
 *
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
export function darken(color: string, coefficient: number) {
  const decomposedColor = decomposeColor(color);
  coefficient = clamp(coefficient);

  if (decomposedColor.type.indexOf("hsl") !== -1) {
    decomposedColor.values[2] *= 1 - coefficient;
  } else if (decomposedColor.type.indexOf("rgb") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposedColor.values[i] *= 1 - coefficient;
    }
  }
  return recomposeColor(decomposedColor);
}

/**
 * Lightens a color.
 *
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
export function lighten(color: string, coefficient: number) {
  if (!color) return color;

  const decomposedColor = decomposeColor(color);
  coefficient = clamp(coefficient);

  if (decomposedColor.type.indexOf("hsl") !== -1) {
    decomposedColor.values[2] += (100 - decomposedColor.values[2]) * coefficient;
  } else if (decomposedColor.type.indexOf("rgb") !== -1) {
    for (let i = 0; i < 3; i += 1) {
      decomposedColor.values[i] += (255 - decomposedColor.values[i]) * coefficient;
    }
  }

  return recomposeColor(decomposedColor);
}

export function luma(color: string) {
  if (!color) return 0;

  const decomposedColor = decomposeColor(color);

  if (decomposedColor.type === "hsl" || decomposedColor.type === "hsla") {
    console.warn("hsl is not supported for luma calculation");

    return 0;
  }

  return (
    (0.2126 * decomposedColor.values[0] +
      0.7152 * decomposedColor.values[1] +
      0.0722 * decomposedColor.values[2]) /
    MAX_LUMA
  );
}
